import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { pickFrEn } from "../lib/texte-i18n";
import { pickScoringLabel } from "../lib/scoring-i18n";
import { Plus, Pencil, Trash2, ChevronUp, ChevronDown, ArrowLeft, Users, X, Award, AlertTriangle, CheckCircle2, Circle, BarChart3, History as HistoryIcon, Hash, GraduationCap } from "lucide-react";
import { authApi, accountApi, certificationPathsApi, bizCoinsApi, pathTemplatesApi, competencyApi, type PathTemplateSummary, type CompetencyPhase } from "../lib/api";
import { ScopeRewardsModeControl } from "../components/ScopeRewardsModeControl";
import { PathCaseAssistant } from "../components/PathCaseAssistant";
import { CompetencyChart } from "../components/CompetencyChart";
import { EvalPhaseSteps } from "../components/EvalPhaseSteps";
import { objectiveLabel, objectiveHint } from "../lib/objective-label";
import { SessionsWorkspace, StatCard, type WorkspaceSection } from "../components/SessionsWorkspace";
import { HistoryPage } from "./HistoryPage";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import type { User } from "../types";

// Objectifs binaires (pas de cible chiffrée — réussi ≥ 1 fois). Aligné sur le
// moteur d'atteinte (progress.service.ts).
const BOOLEAN_OBJECTIVES = new Set(["deal_closed", "pitch_validated", "tension_mastered", "clear_outcome"]);

const SIMULATOR_OBJECTIVES: Record<string, Array<{ value: string; labelKey: string }>> = {
  prospector: [{ value: "most_rdv", labelKey: "challenge.objectiveMostRdv" }],
  discovery: [{ value: "most_needs", labelKey: "challenge.objectiveMostNeeds" }],
  negociator: [{ value: "deal_closed", labelKey: "challenge.objectiveDealClosed" }],
  pitch: [{ value: "pitch_validated", labelKey: "challenge.objectivePitchValidated" }],
  satisfactor: [
    { value: "tension_mastered", labelKey: "challenge.objectiveTensionMastered" },
    { value: "clear_outcome", labelKey: "challenge.objectiveClearOutcome" },
  ],
};

interface Props {
  user: User;
  /** "learner" (Mes parcours/certifs, nav Parcours) ou "manager" (builder, dans Mon compte). */
  mode?: "learner" | "manager";
  onLaunchPhase?: (agent: any, ctx: Record<string, unknown>) => void;
  /** Lien « Historique de l'équipe » (Pilot) de l'onglet Historique — même câblage que la page Historique. */
  onOpenTeamHistory?: () => void;
}

import { canManagePaths } from "../lib/path-manage-access";
// Seuil d'activation du badge public Open Badge (doit rester aligné avec
// OPEN_BADGE_MIN_PHASES côté serveur — certificate.service.ts).
const OPEN_BADGE_MIN_PHASES = 20;

type Phase = {
  id: string;
  sequence: number;
  titleFr: string;
  titleEn?: string | null;
  descriptionFr?: string | null;
  simulatorId: string;
  minSessions: number;
  maxSessions: number;
  primaryObjective: string;
  primaryObjectiveTarget: number | null;
  secondaryObjective: string | null;
  secondaryObjectiveTarget: number | null;
  difficulty: string | null;
  unlocksPhaseId: string | null;
  targetIds: string[];
  caseOrigin?: "none" | "generic" | "custom" | "mixed";
  caseNames?: string[];
  genericTargetId?: string | null;
};

const EMPTY_PHASE = {
  titleFr: "",
  descriptionFr: "",
  simulatorId: "",
  minSessions: 1,
  maxSessions: 10,
  primaryObjective: "",
  primaryObjectiveTarget: "" as string | number,
  secondaryObjective: "",
  secondaryObjectiveTarget: "" as string | number,
  difficulty: "",
  unlocksPhaseId: "",
  targetIds: [] as string[],
};

export function CertificationPathsPage({ user, mode = "learner", onLaunchPhase, onOpenTeamHistory }: Props) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === "en" ? "en" : "fr";
  const isManager = mode === "manager" && canManagePaths(user.profileType);
  const isLearner = mode === "learner";
  const canManage = isManager; // pilote l'affichage du builder

  const [view, setView] = useState<"list" | "detail">("list");
  // Étape 9B — mode de récompense de l'entreprise (pour l'override par parcours).
  const [tenantMode, setTenantMode] = useState<"virtual" | "catalog">("virtual");
  useEffect(() => {
    if (isManager) bizCoinsApi.getWallet().then((w) => setTenantMode(w.mode)).catch(() => {});
  }, [isManager]);
  const [paths, setPaths] = useState<any[]>([]);
  const [mine, setMine] = useState<any[]>([]);
  const [certs, setCerts] = useState<any[]>([]);
  const [available, setAvailable] = useState<any[]>([]);
  const [enrollingId, setEnrollingId] = useState<string | null>(null);
  const [learnerSection, setLearnerSection] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [simulators, setSimulators] = useState<any[]>([]);
  const [targets, setTargets] = useState<any[]>([]);

  // Détail parcours
  const [detail, setDetail] = useState<{ path: any; phases: Phase[]; canManage: boolean; canCompleteCases: boolean } | null>(null);
  const [enrollments, setEnrollments] = useState<any[]>([]);
  const [assignable, setAssignable] = useState<any[]>([]);

  // Modales
  const [pathModal, setPathModal] = useState<{ open: boolean; editing: any | null; titleFr: string; descriptionFr: string; level: number; status: string }>(
    { open: false, editing: null, titleFr: "", descriptionFr: "", level: 1, status: "draft" },
  );
  const [phaseModal, setPhaseModal] = useState<{ open: boolean; editingId: string | null; form: typeof EMPTY_PHASE }>(
    { open: false, editingId: null, form: { ...EMPTY_PHASE } },
  );
  const [assignModal, setAssignModal] = useState<{ open: boolean; selected: string[] }>({ open: false, selected: [] });
  const [deleteConfirm, setDeleteConfirm] = useState<any | null>(null);
  const [deletePhaseConfirm, setDeletePhaseConfirm] = useState<Phase | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Modèles de parcours (instanciation 1 clic)
  const [templateModal, setTemplateModal] = useState<{ open: boolean; list: PathTemplateSummary[]; loading: boolean; instantiatingId: string | null; error: string | null }>(
    { open: false, list: [], loading: false, instantiatingId: null, error: null },
  );
  // Assistant « compléter les cas » (lot 2) — optionnellement ciblé sur une phase.
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [assistantPhaseId, setAssistantPhaseId] = useState<string | null>(null);
  const openAssistant = (phaseId: string | null = null) => { setAssistantPhaseId(phaseId); setAssistantOpen(true); };

  async function openTemplateModal() {
    setTemplateModal({ open: true, list: [], loading: true, instantiatingId: null, error: null });
    try {
      const list = await pathTemplatesApi.list();
      setTemplateModal((m) => ({ ...m, list, loading: false }));
    } catch (e) {
      setTemplateModal((m) => ({ ...m, loading: false, error: e instanceof Error ? e.message : "Erreur" }));
    }
  }

  async function instantiateTemplate(templateId: string) {
    setTemplateModal((m) => ({ ...m, instantiatingId: templateId, error: null }));
    try {
      const { pathId } = await pathTemplatesApi.instantiate(templateId);
      setTemplateModal({ open: false, list: [], loading: false, instantiatingId: null, error: null });
      await reloadPaths();
      await openDetail(pathId);
      openAssistant(); // enchaîne directement sur « compléter les cas »
    } catch (e) {
      setTemplateModal((m) => ({ ...m, instantiatingId: null, error: e instanceof Error ? e.message : "Erreur" }));
    }
  }

  useEffect(() => {
    Promise.all([
      isManager ? certificationPathsApi.list().catch(() => []) : Promise.resolve([]),
      isLearner ? certificationPathsApi.mine().catch(() => []) : Promise.resolve([]),
      isLearner ? certificationPathsApi.myCertifications().catch(() => []) : Promise.resolve([]),
      authApi.getAgents(),
      accountApi.getTargets().catch(() => []),
      isLearner ? certificationPathsApi.available().catch(() => []) : Promise.resolve([]),
    ])
      .then(([p, m, cs, sims, tgs, av]) => {
        setPaths(p);
        setMine(m);
        setCerts(cs);
        setSimulators((sims || []).filter((s: any) => s.simulatorType === "training"));
        setTargets(tgs || []);
        setAvailable(av || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [mode]);

  function objLabel(code: string, simulatorId: string): string {
    return objectiveLabel(code, simulators.find((s) => s.id === simulatorId), t, lang);
  }

  // Vue focalisée « Évaluation » : le parcours d'évaluation a sa propre interface
  // dédiée (en-tête 🎯, uniquement ce parcours, sans le chrome « certifiants »),
  // pas la liste générique. Activée par le deep-link `bizcoach_open_eval` (posé par
  // le dashboard « En cours »/« Tes parcours ») ou par « Tes premiers pas ».
  const [focusEval, setFocusEval] = useState(false);

  // Deep-link « Tes premiers pas » : cibler LE parcours d'évaluation (localStorage posé
  // par FirstStepsCard) → forcer l'onglet Vue d'ensemble, scroller sur la carte, surligner.
  const [focusPathId, setFocusPathId] = useState<string | null>(null);
  useEffect(() => {
    if (loading || !isLearner) return;
    // 1) Ouverture directe de la vue évaluation dédiée.
    let openEval: string | null = null;
    try { openEval = localStorage.getItem("bizcoach_open_eval"); } catch { /* noop */ }
    if (openEval) {
      try { localStorage.removeItem("bizcoach_open_eval"); } catch { /* noop */ }
      if (mine.some((it) => it.path.kind === "evaluation")) {
        setLearnerSection("overview");
        setFocusEval(true);
        return;
      }
    }
    // 2) Deep-link vers un parcours précis (scroll + surlignage). Si c'est le parcours
    //    d'évaluation, on bascule aussi sur la vue dédiée.
    let fid: string | null = null;
    try { fid = localStorage.getItem("bizcoach_focus_path_id"); } catch { /* noop */ }
    if (!fid) return;
    try { localStorage.removeItem("bizcoach_focus_path_id"); } catch { /* noop */ }
    const target = mine.find((it) => it.path.id === fid);
    if (!target) return; // pas (encore) inscrit → on ignore
    setLearnerSection("overview");
    if (target.path.kind === "evaluation") { setFocusEval(true); return; }
    setFocusPathId(fid);
    const scroll = setTimeout(() => {
      document.getElementById(`cert-path-${fid}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 150);
    const clear = setTimeout(() => setFocusPathId(null), 2600);
    return () => { clearTimeout(scroll); clearTimeout(clear); };
  }, [loading, isLearner, mine]);

  // Auto-inscription à un parcours proposé puis bascule sur l'onglet Mes parcours.
  async function enrollInPath(pathId: string) {
    setEnrollingId(pathId);
    try {
      await certificationPathsApi.enrollSelf(pathId);
      const [m, av] = await Promise.all([
        certificationPathsApi.mine().catch(() => mine),
        certificationPathsApi.available().catch(() => available),
      ]);
      setMine(m);
      setAvailable(av);
      setLearnerSection("overview");
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setEnrollingId(null);
    }
  }

  const pickPathDesc = (p: any) => (lang === "en" ? p.descriptionEn || p.descriptionFr : p.descriptionFr) || "";
  const learnerSections: WorkspaceSection[] = focusEval
    ? [{ id: "overview", label: t("evalPath.sectionLabel"), icon: BarChart3 }]
    : [
        { id: "overview", label: t("sessionsTraining.overview"), icon: BarChart3 },
        { id: "enroll", label: t("certPaths.enrollSection"), icon: Plus },
        { id: "history", label: t("sessionsTraining.history"), icon: HistoryIcon },
      ];

  function launchPhase(item: any, phase: any, seq: number) {
    const sim = simulators.find((s) => s.id === phase.simulatorId);
    if (!sim || !onLaunchPhase) return;
    onLaunchPhase({ ...sim, isUnlocked: true }, {
      pathPhaseId: phase.id,
      targetId: (phase.targetIds || [])[0],
      simulatorId: sim.id,
      pathTitle: pickTitle(item.path),
      phaseTitle: phase.titleFr,
      sequence: seq,
      totalPhases: item.phases.length,
      primaryObjectiveLabel: objLabel(phase.primaryObjective, sim.id),
      primaryObjectiveTarget: phase.primaryObjectiveTarget,
      secondaryObjectiveLabel: phase.secondaryObjective ? objLabel(phase.secondaryObjective, sim.id) : "",
      minSessions: phase.minSessions,
      difficulty: phase.difficulty || "", // "" = libre ; sinon niveau imposé/verrouillé
    });
  }

  const reloadPaths = () => certificationPathsApi.list().then(setPaths).catch(() => {});
  const reloadMine = () => certificationPathsApi.mine().then(setMine).catch(() => {});

  // ── Lancer / Reporter / Refuser (apprenant) ──
  const [evalBusy, setEvalBusy] = useState(false);
  const [postponeOpen, setPostponeOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState("");

  function firstLaunchablePhase(item: any) {
    return item.phases.find((p: any) => p.accessible && (p.progress?.status ?? "todo") !== "done") || item.phases[0] || null;
  }
  async function handleLaunchEval(item: any) {
    const ph = firstLaunchablePhase(item);
    if (!ph) return;
    if (item.enrollment.status === "postponed" || item.enrollment.status === "declined") {
      try { await certificationPathsApi.reactivate(item.path.id); } catch { /* on lance quand même */ }
    }
    launchPhase(item, ph, item.phases.indexOf(ph) + 1);
  }
  async function handlePostponeEval(item: any, until: Date) {
    if (isNaN(until.getTime()) || until.getTime() <= Date.now()) return;
    setEvalBusy(true);
    try { await certificationPathsApi.postpone(item.path.id, until.toISOString()); await reloadMine(); setPostponeOpen(false); }
    catch (e) { alert((e as Error).message); }
    finally { setEvalBusy(false); }
  }
  async function handleDeclineEval(item: any) {
    if (!declineReason.trim()) return;
    setEvalBusy(true);
    try { await certificationPathsApi.decline(item.path.id, declineReason.trim()); await reloadMine(); setDeclineOpen(false); setDeclineReason(""); }
    catch (e) { alert((e as Error).message); }
    finally { setEvalBusy(false); }
  }
  async function handleReactivateEval(item: any) {
    setEvalBusy(true);
    try { await certificationPathsApi.reactivate(item.path.id); await reloadMine(); }
    catch (e) { alert((e as Error).message); }
    finally { setEvalBusy(false); }
  }

  async function openDetail(id: string) {
    const d = await certificationPathsApi.get(id);
    setDetail(d);
    setView("detail");
    certificationPathsApi.listEnrollments(id).then(setEnrollments).catch(() => setEnrollments([]));
    certificationPathsApi.getAssignableUsers(id).then(setAssignable).catch(() => setAssignable([]));
  }

  // ---- Parcours CRUD ----
  function openCreatePath() {
    setPathModal({ open: true, editing: null, titleFr: "", descriptionFr: "", level: 1, status: "draft" });
  }
  function openEditPath(p: any) {
    setPathModal({ open: true, editing: p, titleFr: p.titleFr || "", descriptionFr: p.descriptionFr || "", level: p.level || 1, status: p.status || "draft" });
  }
  async function savePath() {
    if (!pathModal.titleFr.trim()) return;
    const payload = { titleFr: pathModal.titleFr.trim(), descriptionFr: pathModal.descriptionFr, level: pathModal.level, status: pathModal.status };
    if (pathModal.editing) await certificationPathsApi.update(pathModal.editing.id, payload);
    else await certificationPathsApi.create(payload);
    setPathModal((m) => ({ ...m, open: false }));
    await reloadPaths();
  }
  async function confirmDeletePath() {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await certificationPathsApi.remove(deleteConfirm.id);
      setDeleteConfirm(null);
      setView("list");
      setDetail(null);
      await reloadPaths();
    } catch {
      /* l'erreur reste affichée par le bouton réactivé */
    } finally {
      setDeleting(false);
    }
  }

  // ---- Phases CRUD ----
  function openCreatePhase() {
    setPhaseModal({ open: true, editingId: null, form: { ...EMPTY_PHASE } });
  }
  function openEditPhase(ph: Phase) {
    setPhaseModal({
      open: true,
      editingId: ph.id,
      form: {
        titleFr: ph.titleFr || "",
        descriptionFr: ph.descriptionFr || "",
        simulatorId: ph.simulatorId || "",
        minSessions: ph.minSessions ?? 1,
        maxSessions: ph.maxSessions ?? 10,
        primaryObjective: ph.primaryObjective || "",
        primaryObjectiveTarget: ph.primaryObjectiveTarget ?? "",
        secondaryObjective: ph.secondaryObjective || "",
        secondaryObjectiveTarget: ph.secondaryObjectiveTarget ?? "",
        difficulty: ph.difficulty || "",
        unlocksPhaseId: ph.unlocksPhaseId || "",
        targetIds: ph.targetIds || [],
      },
    });
  }
  async function savePhase() {
    if (!detail) return;
    const f = phaseModal.form;
    if (!f.titleFr.trim() || !f.simulatorId || !f.primaryObjective) return;
    const toInt = (v: string | number) => (v === "" || v === null ? null : parseInt(String(v), 10));
    const payload: Record<string, unknown> = {
      titleFr: f.titleFr.trim(),
      descriptionFr: f.descriptionFr,
      simulatorId: f.simulatorId,
      minSessions: Number(f.minSessions) || 1,
      maxSessions: Number(f.maxSessions) || 10,
      primaryObjective: f.primaryObjective,
      primaryObjectiveTarget: BOOLEAN_OBJECTIVES.has(f.primaryObjective) ? null : toInt(f.primaryObjectiveTarget),
      secondaryObjective: f.secondaryObjective || null,
      secondaryObjectiveTarget: f.secondaryObjective && !BOOLEAN_OBJECTIVES.has(f.secondaryObjective) ? toInt(f.secondaryObjectiveTarget) : null,
      difficulty: f.difficulty || null,
      unlocksPhaseId: f.unlocksPhaseId || null,
      targetIds: f.targetIds,
    };
    if (phaseModal.editingId) await certificationPathsApi.updatePhase(phaseModal.editingId, payload);
    else await certificationPathsApi.createPhase(detail.path.id, payload);
    setPhaseModal((m) => ({ ...m, open: false }));
    await openDetail(detail.path.id);
  }
  async function confirmDeletePhase() {
    if (!detail || !deletePhaseConfirm) return;
    setDeleting(true);
    try {
      await certificationPathsApi.deletePhase(deletePhaseConfirm.id);
      setDeletePhaseConfirm(null);
      await openDetail(detail.path.id);
    } catch {
      /* noop */
    } finally {
      setDeleting(false);
    }
  }
  async function movePhase(ph: Phase, dir: -1 | 1) {
    if (!detail) return;
    const ordered = [...detail.phases].sort((a, b) => a.sequence - b.sequence);
    const idx = ordered.findIndex((p) => p.id === ph.id);
    const swap = ordered[idx + dir];
    if (!swap) return;
    await Promise.all([
      certificationPathsApi.updatePhase(ph.id, { sequence: swap.sequence }),
      certificationPathsApi.updatePhase(swap.id, { sequence: ph.sequence }),
    ]);
    await openDetail(detail.path.id);
  }
  // Override explicite : rétablir le cas générique Kestio d'une phase.
  async function revertToGeneric(ph: Phase) {
    if (!detail || !ph.genericTargetId) return;
    await certificationPathsApi.updatePhase(ph.id, { targetIds: [ph.genericTargetId] });
    await openDetail(detail.path.id);
  }

  // ---- Assignation ----
  async function reloadAssignment() {
    if (!detail) return;
    const [en, asg] = await Promise.all([
      certificationPathsApi.listEnrollments(detail.path.id),
      certificationPathsApi.getAssignableUsers(detail.path.id),
    ]);
    setEnrollments(en);
    setAssignable(asg);
  }
  async function confirmAssign() {
    if (!detail || assignModal.selected.length === 0) return;
    await certificationPathsApi.assign(detail.path.id, assignModal.selected);
    setAssignModal({ open: false, selected: [] });
    await reloadAssignment();
  }
  async function removeAssignment(userId: string) {
    if (!detail) return;
    await certificationPathsApi.unassign(detail.path.id, userId);
    await reloadAssignment();
  }

  // Objectifs disponibles selon le simulateur de la phase en cours d'édition.
  const availableObjectives = useMemo(() => {
    const sim = simulators.find((s) => s.id === phaseModal.form.simulatorId);
    const simName = (sim?.name || "").toLowerCase();
    const specific = (SIMULATOR_OBJECTIVES[simName] || []).map((o) => ({ value: o.value, label: t(o.labelKey) }));
    const scoring = (sim?.scoringCriteria || [])
      .filter((c: any) => c.type === "score" && !["overall", "global", "score_total", "total_score"].includes(c.key))
      .map((c: any) => ({ value: c.key, label: pickScoringLabel(c, lang) || c.key }));
    const common = [
      { value: "cumulative_score", label: t("challenge.objectiveCumulativeScore") },
      { value: "best_average", label: t("challenge.objectiveBestAverage") },
    ];
    return [...specific, ...scoring, ...common];
  }, [phaseModal.form.simulatorId, simulators, lang, t]);

  const phaseTargets = useMemo(
    () => targets.filter((tg) => !phaseModal.form.simulatorId || tg.simulatorId === phaseModal.form.simulatorId),
    [targets, phaseModal.form.simulatorId],
  );

  const pickTitle = (o: any) => (lang === "fr" ? o.titleFr : o.titleEn) || o.titleFr || o.titleEn || "";

  // ====================================================================
  // Rendu
  // ====================================================================
  if (loading) {
    return <div className={`${isManager ? "py-4" : "max-w-5xl mx-auto py-10 px-4"} text-muted-foreground`}>{t("common.loading")}</div>;
  }

  return (
    <div data-tour="paths-view" className={isManager ? "" : isLearner ? "" : "max-w-5xl mx-auto py-6 px-4"}>
      {view === "list" && (
        <>
          {isLearner && (
            <SessionsWorkspace
              title={focusEval ? t("evalPath.workspaceTitle") : t("certPaths.title")}
              subtitle={focusEval ? t("evalPath.workspaceSubtitle") : t("certPaths.learnerSubtitle")}
              accent="#5A4BBE"
              headerIcon={focusEval ? "🎯" : "🎓"}
              sections={learnerSections}
              active={learnerSection}
              onChange={setLearnerSection}
            >
              {/* KPIs overview retirés (doublon de la page Entraînement — hub des engagements). */}
              {learnerSection === "enroll" && (
                available.length === 0 ? (
                  <div className="rounded-2xl border border-dashed py-16 text-center text-sm text-muted-foreground">{t("certPaths.noAvailable")}</div>
                ) : (
                  <div className="grid gap-3 sm:grid-cols-2">
                    {available.map((p: any) => (
                      <div key={p.id} className="flex flex-col rounded-xl border p-4">
                        <div className="flex items-center gap-2">
                          <Award className="h-5 w-5 shrink-0" style={{ color: "#988EF2" }} />
                          <h3 className="font-semibold" style={{ color: "#384872" }}>{pickTitle(p)}</h3>
                          {p.origin === "kestio_catalogue" && <span className="text-xs font-medium text-violet-600">Kestio</span>}
                        </div>
                        <p className="mb-3 mt-1 flex-1 text-xs text-muted-foreground line-clamp-2">{pickPathDesc(p)}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">{t("certPaths.phasesProgress", { done: 0, total: p.phaseCount })}</span>
                          <Button size="sm" disabled={enrollingId === p.id} onClick={() => enrollInPath(p.id)}>
                            <Plus className="h-4 w-4 mr-1" />{t("certPaths.enroll")}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}
              {learnerSection === "overview" && (
                <div className="mt-6">
          {/* Vue focalisée « Évaluation » : lien retour vers tous les parcours */}
          {focusEval && (
            <button onClick={() => setFocusEval(false)} className="mb-4 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
              ← {t("evalPath.backToAllPaths")}
            </button>
          )}
          {/* Barre de décision « Lancer / Reporter / Refuser » — parcours d'évaluation non commencé */}
          {focusEval && (() => {
            const ev = mine.find((it) => it.path.kind === "evaluation");
            if (!ev) return null;
            const st = ev.enrollment.status as string;
            if (st === "in_progress" || st === "completed") return null;
            const postponedUntil = ev.enrollment.postponedUntil ? new Date(ev.enrollment.postponedUntil) : null;
            const isPostponed = st === "postponed" && postponedUntil && postponedUntil.getTime() > Date.now();
            const isDeclined = st === "declined";
            return (
              <div className="mb-6 rounded-2xl border p-4" style={{ borderColor: "#988EF2", background: "#FBFAFE" }}>
                {isPostponed && (
                  <p className="mb-3 text-sm" style={{ color: "#5A4BBE" }}>
                    🕒 {t("evalPath.postponedUntil", { date: postponedUntil!.toLocaleDateString() })}
                  </p>
                )}
                {isDeclined && (
                  <p className="mb-3 text-sm" style={{ color: "#C0423F" }}>
                    ✕ {t("evalPath.declinedLabel")}{ev.enrollment.declineReason ? ` — « ${ev.enrollment.declineReason} »` : ""}
                  </p>
                )}
                {!isPostponed && !isDeclined && (
                  <p className="mb-3 text-sm text-muted-foreground">{t("evalPath.decisionIntro")}</p>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <Button size="sm" disabled={evalBusy} onClick={() => handleLaunchEval(ev)}>
                    {isPostponed || isDeclined ? t("evalPath.launchNow") : t("evalPath.launch")}
                  </Button>
                  {!isDeclined && (
                    <div className="relative">
                      <Button size="sm" variant="outline" disabled={evalBusy} onClick={() => { setPostponeOpen((v) => !v); setDeclineOpen(false); }}>
                        {t("evalPath.postpone")}
                      </Button>
                      {postponeOpen && (
                        <div className="absolute z-20 mt-1 w-56 rounded-xl border bg-white p-2 shadow-lg">
                          {[{ d: 3, k: "postpone3d" }, { d: 7, k: "postpone7d" }, { d: 14, k: "postpone14d" }].map((o) => (
                            <button key={o.d} disabled={evalBusy}
                              className="block w-full rounded-lg px-3 py-1.5 text-left text-sm hover:bg-slate-50"
                              onClick={() => handlePostponeEval(ev, new Date(Date.now() + o.d * 86400000))}>
                              {t(`evalPath.${o.k}`)}
                            </button>
                          ))}
                          <div className="mt-1 border-t pt-2">
                            <label className="mb-1 block px-1 text-[11px] text-muted-foreground">{t("evalPath.postponeCustom")}</label>
                            <input type="date" disabled={evalBusy}
                              min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                              className="w-full rounded-lg border px-2 py-1 text-sm"
                              onChange={(e) => { if (e.target.value) handlePostponeEval(ev, new Date(e.target.value + "T09:00:00")); }} />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {!isDeclined && (
                    <Button size="sm" variant="ghost" disabled={evalBusy} className="text-muted-foreground" onClick={() => { setDeclineOpen(true); setPostponeOpen(false); }}>
                      {t("evalPath.decline")}
                    </Button>
                  )}
                </div>
                {/* Modale de refus (motif obligatoire) */}
                {declineOpen && (
                  <div className="mt-3 rounded-xl border bg-white p-3">
                    <label className="mb-1 block text-sm font-medium" style={{ color: "#384872" }}>{t("evalPath.declineReasonLabel")}</label>
                    <textarea value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} rows={2}
                      placeholder={t("evalPath.declineReasonPlaceholder")}
                      className="w-full rounded-lg border px-2 py-1.5 text-sm" />
                    <div className="mt-2 flex justify-end gap-2">
                      <Button size="sm" variant="ghost" disabled={evalBusy} onClick={() => { setDeclineOpen(false); setDeclineReason(""); }}>{t("common.cancel")}</Button>
                      <Button size="sm" disabled={evalBusy || !declineReason.trim()} onClick={() => handleDeclineEval(ev)}>{t("evalPath.declineConfirm")}</Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
          {/* Mes certifications obtenues (masquées en vue évaluation dédiée) */}
          {!focusEval && certs.length > 0 && (
            <div className="mb-10">
              <h2 className="font-semibold text-lg mb-3" style={{ color: "#384872" }}>{t("certPaths.myCertifications")}</h2>
              <div className="grid gap-3 sm:grid-cols-2">
                {certs.map((c) => (
                  <a
                    key={c.publicId}
                    href={`/certificate/${c.publicId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 rounded-xl border p-4 transition hover:shadow-sm"
                    style={{ borderColor: "#7CBF94", background: "#7CBF9412" }}
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full" style={{ background: "#7CBF94" }}>
                      <Award className="h-5 w-5 text-white" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold truncate" style={{ color: "#384872" }}>{c.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {t("certPaths.certifiedOn")} {new Date(c.issuedAt).toLocaleDateString()}
                        {c.mention === "excellence" ? ` · ★ ${t("certPaths.excellence")}` : ""}
                      </p>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Vue apprenant — Mes parcours (filtrés au parcours d'évaluation en vue dédiée) */}
          {(focusEval ? mine.filter((it) => it.path.kind === "evaluation") : mine).length > 0 && (
            <div className="mb-10">
              {!focusEval && <h2 className="font-semibold text-lg mb-3" style={{ color: "#384872" }}>{t("certPaths.myPaths")}</h2>}
              <div className="space-y-4">
                {(focusEval ? mine.filter((it) => it.path.kind === "evaluation") : mine).map((item) => {
                  const total = item.phases.length;
                  const doneCount = item.phases.filter((p: any) => p.progress?.status === "done").length;
                  const completed = item.enrollment.status === "completed";
                  const pct = total ? Math.round((doneCount / total) * 100) : 0;
                  const isEval = item.path.kind === "evaluation";
                  return (
                    <div
                      key={item.enrollment.id}
                      id={`cert-path-${item.path.id}`}
                      className="rounded-xl border p-4 transition-shadow"
                      style={focusPathId === item.path.id ? { boxShadow: "0 0 0 3px #988EF2" } : undefined}
                    >
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <h3 className="font-semibold truncate" style={{ color: "#384872" }}>{pickTitle(item.path)}</h3>
                          {isEval && <span className="text-xs rounded-full px-2 py-0.5 shrink-0" style={{ background: "#EEE9FB", color: "#5A4BBE" }}>{t("certPaths.kind.evaluation")}</span>}
                        </div>
                        <span className={`text-xs rounded-full px-2 py-0.5 ${completed ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}`}>
                          {t(`certPaths.enrollStatus.${item.enrollment.status}`)}
                        </span>
                      </div>
                      {isEval && !completed && (
                        <p className="mb-3 text-xs text-muted-foreground">{t("evalPath.hint")}</p>
                      )}
                      {isEval && completed && (
                        <div className="mb-4 rounded-xl border p-3" style={{ borderColor: "#988EF2", background: "#FBFAFE" }}>
                          <div className="mb-2 flex items-center gap-2">
                            <span className="text-sm font-semibold" style={{ color: "#5A4BBE" }}>🧭 {t("evalPath.diagnosticTitle")}</span>
                          </div>
                          <p className="mb-3 text-xs text-muted-foreground">{t("evalPath.diagnosticIntro")}</p>
                          <EvalDiagnostic pathId={item.path.id} />
                        </div>
                      )}
                      {/* Barre de progression globale */}
                      <div className="mb-3 flex items-center gap-2">
                        <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: completed ? "#7CBF94" : "#988EF2" }} />
                        </div>
                        <span className="shrink-0 text-xs font-medium text-muted-foreground">{t("certPaths.phasesProgress", { done: doneCount, total })}</span>
                      </div>
                      {isEval ? (
                        <EvalPhaseSteps
                          phases={item.phases}
                          simulators={simulators}
                          pickTitle={pickTitle}
                          onLaunch={(ph, n) => launchPhase(item, ph, n)}
                        />
                      ) : (
                      <div className="space-y-2">
                        {item.phases.map((ph: any, i: number) => {
                          const st = ph.progress?.status || "todo";
                          const sim = simulators.find((s) => s.id === ph.simulatorId);
                          const canStart = ph.accessible && st !== "done";
                          const prog = ph.progress;
                          const sessCount = prog?.sessionsCount ?? 0;
                          const remainingSessions = Math.max(0, (ph.minSessions ?? 0) - sessCount);
                          const primaryLabel = objLabel(ph.primaryObjective, ph.simulatorId);
                          const hasTarget = ph.primaryObjectiveTarget != null;
                          const primaryOk = !!prog?.primaryAchieved;
                          const showDetail = ph.accessible || st !== "todo";
                          const primaryHint = objectiveHint(ph.primaryObjective, ph.primaryObjectiveTarget, t);
                          // Barre de progression de la phase = moyenne des 2 conditions
                          // (volume de sessions + objectif principal).
                          const sessRatio = (ph.minSessions ?? 0) > 0 ? Math.min(1, sessCount / ph.minSessions) : 1;
                          const objRatio = primaryOk
                            ? 1
                            : hasTarget && ph.primaryObjectiveTarget > 0
                              ? Math.min(1, (prog?.primaryValue ?? 0) / ph.primaryObjectiveTarget)
                              : 0;
                          const phasePct = st === "done" ? 100 : Math.round(((sessRatio + objRatio) / 2) * 100);
                          return (
                            <div key={ph.id} className={`rounded-lg border px-3 py-2 ${st === "done" ? "bg-green-50/40" : ph.accessible ? "" : "opacity-60"}`}>
                              <div className="flex items-center gap-3">
                                <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${st === "done" ? "bg-green-100 text-green-700" : ph.accessible ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-400"}`}>{st === "done" ? "✓" : i + 1}</div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate">{ph.titleFr}</p>
                                  <p className="text-xs text-muted-foreground truncate">{sim?.name} · {t(`certPaths.phaseStatus.${st}`)}</p>
                                </div>
                                {canStart ? (
                                  <Button size="sm" onClick={() => launchPhase(item, ph, i + 1)}>{t("certPaths.start")}</Button>
                                ) : st === "done" ? null : (
                                  <span className="text-xs text-muted-foreground">🔒 {t("certPaths.locked")}</span>
                                )}
                              </div>
                              {showDetail && (
                                <div className="mt-2 pl-9 text-xs">
                                  {/* Barre de progression de la phase */}
                                  <div className="mb-2 flex items-center gap-2">
                                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                                      <div className="h-full rounded-full transition-all" style={{ width: `${phasePct}%`, background: st === "done" ? "#7CBF94" : "#988EF2" }} />
                                    </div>
                                    <span className="shrink-0 text-[11px] font-medium text-muted-foreground">{phasePct}%</span>
                                  </div>
                                  {st !== "done" && <p className="mb-1 font-medium text-slate-500">{t("certPaths.toComplete")}</p>}
                                  <ul className="space-y-1">
                                    {/* Condition 1 : volume de sessions */}
                                    <li className="flex items-center gap-1.5">
                                      {remainingSessions === 0
                                        ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-green-600" />
                                        : <Circle className="h-3.5 w-3.5 shrink-0 text-slate-300" />}
                                      <span className={remainingSessions === 0 ? "text-green-700" : "text-muted-foreground"}>
                                        {t("certPaths.checkSessions", { done: sessCount, min: ph.minSessions })}
                                      </span>
                                    </li>
                                    {/* Condition 2 : objectif principal */}
                                    <li className="flex items-start gap-1.5">
                                      {primaryOk
                                        ? <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-green-600" />
                                        : <Circle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-300" />}
                                      <span className="min-w-0">
                                        <span className={primaryOk ? "text-green-700" : "text-muted-foreground"}>
                                          {t("certPaths.checkObjective", { label: primaryLabel })}
                                          {hasTarget ? ` (${prog?.primaryValue ?? 0}/${ph.primaryObjectiveTarget})` : ""}
                                          {" — "}
                                          <span className={primaryOk ? "font-medium text-green-700" : "italic text-muted-foreground"}>
                                            {primaryOk ? t("certPaths.reached") : t("certPaths.notReached")}
                                          </span>
                                        </span>
                                        {primaryHint && <span className="mt-0.5 block text-[11px] italic text-slate-400">{primaryHint}</span>}
                                      </span>
                                    </li>
                                  </ul>
                                  {prog?.bestScore != null && prog.bestScore > 0 && (
                                    <p className="mt-1 text-muted-foreground">{t("certPaths.bestScore")} : {prog.bestScore}</p>
                                  )}
                                  {st === "in_progress" && (
                                    <p className="mt-1.5 font-medium" style={{ color: "#5A4BBE" }}>
                                      → {!primaryOk ? t("certPaths.keepGoing") : remainingSessions > 0 ? t("certPaths.keepSessions", { n: remainingSessions }) : t("certPaths.remainingNothing")}
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {mine.length === 0 && certs.length === 0 && (
            <div className="rounded-2xl border border-dashed py-16 text-center text-sm text-muted-foreground">{t("certPaths.noAssignedPath")}</div>
          )}
                </div>
              )}
              {learnerSection === "history" && (
                <HistoryPage
                  user={user}
                  embedded
                  onlyTab="parcours"
                  onBack={() => setLearnerSection("overview")}
                  onNavigate={() => { /* noop */ }}
                  onOpenTeamHistory={onOpenTeamHistory}
                />
              )}
            </SessionsWorkspace>
          )}

          {/* Vue manager — gestion des parcours */}
          {canManage && (
            <>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-lg" style={{ color: "#384872" }}>{t("certPaths.manage")}</h2>
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={openTemplateModal}><GraduationCap className="h-4 w-4 mr-1.5" />{t("certPaths.fromTemplate")}</Button>
                  <Button onClick={openCreatePath}><Plus className="h-4 w-4 mr-1.5" />{t("certPaths.createPath")}</Button>
                </div>
              </div>
              {paths.length === 0 ? (
                <div className="text-center text-muted-foreground py-16 border rounded-xl">{t("certPaths.empty")}</div>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                  {paths.map((p) => (
                    <div key={p.id} className="rounded-xl border p-4 hover:shadow-sm transition cursor-pointer" onClick={() => openDetail(p.id)}>
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-semibold" style={{ color: "#384872" }}>{pickTitle(p)}</h3>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className="text-xs rounded-full px-2 py-0.5 bg-slate-100 text-slate-600">{t(`certPaths.status.${p.status}`)}</span>
                          {p.kind === "evaluation" ? (
                            <span className="text-xs rounded-full px-2 py-0.5" style={{ background: "#EEE9FB", color: "#5A4BBE" }} title={t("certPaths.systemLockedHint")}>🔒 {t("certPaths.systemBadge")}</span>
                          ) : (
                            <button
                              onClick={(e) => { e.stopPropagation(); setDeleteConfirm(p); }}
                              className="rounded p-1 hover:bg-red-50"
                              title={t("common.delete")}
                            >
                              <Trash2 className="h-4 w-4 text-red-400" />
                            </button>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{(lang === "fr" ? p.descriptionFr : p.descriptionEn) || p.descriptionFr || ""}</p>
                      <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
                        <span>{t("certPaths.level")} {p.level}</span>
                        {p.origin === "kestio_catalogue" && <span className="text-violet-600 font-medium">Kestio</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}

      {view === "detail" && detail && (
        <>
          <button onClick={() => { setView("list"); setDetail(null); }} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ArrowLeft className="h-4 w-4" />{t("common.back")}
          </button>

          <div className="flex items-start justify-between gap-3 mb-6">
            <div>
              <h1 className="text-2xl font-bold" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>{pickTitle(detail.path)}</h1>
              <p className="text-sm text-muted-foreground mt-1">{detail.path.descriptionFr}</p>
            </div>
            {(detail.canManage || detail.canCompleteCases) && (
              <div className="flex gap-2 shrink-0">
                {detail.canCompleteCases && detail.phases.length > 0 && (
                  <Button size="sm" onClick={() => openAssistant()}><GraduationCap className="h-4 w-4 mr-1" />{t("pathAssistant.openButton")}</Button>
                )}
                {detail.canManage && <Button variant="outline" size="sm" onClick={() => openEditPath(detail.path)}><Pencil className="h-4 w-4 mr-1" />{t("common.edit")}</Button>}
                {detail.canManage && <Button variant="outline" size="sm" onClick={() => setDeleteConfirm(detail.path)}><Trash2 className="h-4 w-4 mr-1 text-red-500" />{t("common.delete")}</Button>}
              </div>
            )}
          </div>

          {/* Évaluation : cas génériques par défaut + possibilité d'override explicite. */}
          {detail.path.kind === "evaluation" && detail.canCompleteCases && (
            <div className="mb-5 rounded-xl border p-3.5 text-xs" style={{ borderColor: "#988EF2", background: "#F6F4FE", color: "#384872" }}>
              🧭 {t("certPaths.evalComparabilityNote")}
            </div>
          )}

          {/* Rappel : phases sans cas rattaché */}
          {detail.canCompleteCases && (() => {
            const missing = detail.phases.filter((p) => !(p.targetIds && p.targetIds.length > 0)).length;
            if (missing === 0) return null;
            return (
              <div className="mb-5 flex items-center justify-between gap-3 rounded-xl border p-3.5" style={{ borderColor: "#F6C847", background: "#FEF9EC" }}>
                <p className="text-sm" style={{ color: "#926B00" }}>{t("pathAssistant.missingBanner", { count: missing })}</p>
                <Button size="sm" onClick={() => openAssistant()}>{t("pathAssistant.openButton")}</Button>
              </div>
            );
          })()}

          {/* Badge public (Open Badge) — éligibilité selon le nombre de phases.
              Sans objet pour un parcours d'évaluation (aucun certificat émis). */}
          {detail.path.kind !== "evaluation" && (() => {
            const count = detail.phases.length;
            const eligible = count >= OPEN_BADGE_MIN_PHASES;
            const pct = Math.min(100, Math.round((count / OPEN_BADGE_MIN_PHASES) * 100));
            return (
              <div className="mb-5 rounded-xl border p-4" style={eligible ? { borderColor: "#7CBF94", background: "#7CBF9412" } : { borderColor: "#988EF2", background: "#988EF210" }}>
                <div className="flex items-center gap-2">
                  <Award className="h-5 w-5" style={{ color: eligible ? "#1E7A3C" : "#5A4BBE" }} />
                  <span className="font-semibold" style={{ color: "#384872" }}>{t("certPaths.openBadgeTitle")}</span>
                  <span className="ml-auto text-sm font-medium" style={{ color: eligible ? "#1E7A3C" : "#5A4BBE" }}>
                    {count}/{OPEN_BADGE_MIN_PHASES} {t("certPaths.phasesWord")}
                  </span>
                </div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: eligible ? "#7CBF94" : "#988EF2" }} />
                </div>
                <p className="mt-2 text-xs" style={{ color: eligible ? "#1E7A3C" : "#6B7280" }}>
                  {eligible
                    ? t("certPaths.openBadgeOn")
                    : t("certPaths.openBadgeOff", { n: OPEN_BADGE_MIN_PHASES - count })}
                </p>
              </div>
            );
          })()}

          {/* Étape 9B — override du mode de récompense pour CE parcours (organisateur). */}
          {detail.canManage && (
            <div className="mb-5">
              <ScopeRewardsModeControl
                scope="path"
                id={detail.path.id}
                initialMode={detail.path.rewardsMode ?? null}
                tenantMode={tenantMode}
              />
            </div>
          )}

          {/* Phases */}
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-lg" style={{ color: "#384872" }}>{t("certPaths.phases")}</h2>
            {detail.canManage && <Button size="sm" onClick={openCreatePhase}><Plus className="h-4 w-4 mr-1" />{t("certPaths.addPhase")}</Button>}
          </div>
          {detail.phases.length === 0 ? (
            <div className="text-center text-muted-foreground py-10 border rounded-xl mb-8">{t("certPaths.noPhase")}</div>
          ) : (
            <div className="space-y-2.5 mb-8">
              {[...detail.phases].sort((a, b) => a.sequence - b.sequence).map((ph, i, arr) => {
                const sim = simulators.find((s) => s.id === ph.simulatorId);
                const hasCase = !!(ph.targetIds && ph.targetIds.length > 0);
                const sessLabel = ph.minSessions === ph.maxSessions
                  ? t("certPaths.sessionCount", { count: ph.minSessions })
                  : t("certPaths.sessionsRange", { min: ph.minSessions, max: ph.maxSessions });
                const objLbl = objLabel(ph.primaryObjective, ph.simulatorId) + (ph.primaryObjectiveTarget != null ? ` ≥ ${ph.primaryObjectiveTarget}` : "");
                return (
                  <div key={ph.id} className="rounded-xl border p-4" style={{ borderColor: hasCase ? "#E6E4EE" : "#F0D890", background: hasCase ? "#fff" : "#FEFBF2" }}>
                    <div className="flex items-start gap-3">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold" style={{ background: "#EDE9FB", color: "#5A4BBE" }}>{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold" style={{ color: "#384872" }}>{ph.titleFr}</p>
                          {hasCase
                            ? <span className="text-[11px] font-bold rounded-full px-2 py-0.5" style={{ background: "#E4F1E9", color: "#1E7A3C" }}>✅ {t("certPaths.phaseReady")}</span>
                            : <span className="text-[11px] font-bold rounded-full px-2 py-0.5" style={{ background: "#FBF3DA", color: "#926B00" }}>⚠️ {t("certPaths.phaseToComplete")}</span>}
                        </div>
                        <div className="mt-1.5 flex flex-wrap gap-x-3.5 gap-y-1 text-xs text-muted-foreground">
                          <span>🎙 {sim?.name}</span>
                          <span>🗓 {sessLabel}</span>
                          <span>🎯 {objLbl}</span>
                        </div>
                        {(() => {
                          const o = ph.caseOrigin ?? (hasCase ? "custom" : "none");
                          if (o === "none") return <div className="mt-1.5 text-xs font-medium" style={{ color: "#926B00" }}>📋 {t("certPaths.caseToDefine")}</div>;
                          if (o === "generic") return <div className="mt-1.5 text-xs font-medium" style={{ color: "#5A4BBE" }}>📋 {t("certPaths.caseGeneric")}</div>;
                          const names = ph.caseNames && ph.caseNames.length ? ph.caseNames.join(", ") : "";
                          return <div className="mt-1.5 text-xs font-medium" style={{ color: "#1E7A3C" }}>📋 {names ? `${names} · ` : ""}{t("certPaths.caseCustom")}</div>;
                        })()}
                      </div>
                      <div className="flex items-start gap-1 shrink-0">
                        {/* Override explicite (parcours système) : personnaliser / rétablir le générique. */}
                        {detail.canCompleteCases && !detail.canManage && (() => {
                          const o = ph.caseOrigin ?? (hasCase ? "custom" : "none");
                          return (
                            <div className="flex flex-col items-end gap-1">
                              <button onClick={() => openAssistant(ph.id)} className="rounded-lg px-2 py-1.5 text-xs font-semibold whitespace-nowrap" style={{ background: "#EDE9FB", color: "#5A4BBE" }}>
                                <Pencil className="h-3.5 w-3.5 inline mr-1" />{o === "none" ? t("certPaths.completeCase") : t("certPaths.customize")}
                              </button>
                              {(o === "custom" || o === "mixed") && ph.genericTargetId && (
                                <button onClick={() => revertToGeneric(ph)} className="text-[11px] text-muted-foreground hover:text-foreground underline whitespace-nowrap">{t("certPaths.revertGeneric")}</button>
                              )}
                            </div>
                          );
                        })()}
                        {detail.canManage && (
                          <>
                            <button disabled={i === 0} onClick={() => movePhase(ph, -1)} className="p-1 disabled:opacity-30"><ChevronUp className="h-4 w-4" /></button>
                            <button disabled={i === arr.length - 1} onClick={() => movePhase(ph, 1)} className="p-1 disabled:opacity-30"><ChevronDown className="h-4 w-4" /></button>
                            <button onClick={() => openEditPhase(ph)} title={t("common.edit")} className="p-1"><Pencil className="h-4 w-4 text-slate-500" /></button>
                            <button onClick={() => setDeletePhaseConfirm(ph)} className="p-1"><Trash2 className="h-4 w-4 text-red-400" /></button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Assignation — tableau des assignés + ajout via modale */}
          {detail.canManage && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-semibold text-lg flex items-center gap-2" style={{ color: "#384872" }}>
                  <Users className="h-5 w-5" />{t("certPaths.assignment")}
                </h2>
                <Button size="sm" onClick={() => setAssignModal({ open: true, selected: [] })}>
                  <Plus className="h-4 w-4 mr-1" />{t("certPaths.addAssignment")}
                </Button>
              </div>
              {enrollments.length === 0 ? (
                <div className="text-center text-muted-foreground py-8 border rounded-xl text-sm">{t("certPaths.noAssigned")}</div>
              ) : (
                <div className="overflow-hidden rounded-xl border">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-left text-xs text-slate-500">
                      <tr>
                        <th className="px-3 py-2 font-medium">{t("certPaths.colName")}</th>
                        <th className="px-3 py-2 font-medium hidden sm:table-cell">{t("certPaths.colEmail")}</th>
                        <th className="px-3 py-2 font-medium">{t("certPaths.colStatus")}</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {enrollments.map((e) => (
                        <tr key={e.userId} className="border-t">
                          <td className="px-3 py-2 font-medium">{e.firstName} {e.lastName}</td>
                          <td className="px-3 py-2 text-muted-foreground hidden sm:table-cell">{e.email}</td>
                          <td className="px-3 py-2">
                            <span className="text-xs rounded-full px-2 py-0.5 bg-slate-100 text-slate-600">{t(`certPaths.enrollStatus.${e.status}`)}</span>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <button onClick={() => removeAssignment(e.userId)} className="p-1" title={t("certPaths.unassign")}>
                              <Trash2 className="h-4 w-4 text-red-400" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ===== Modale Parcours ===== */}
      {pathModal.open && (
        <ModalShell title={pathModal.editing ? t("certPaths.editPath") : t("certPaths.createPath")} onClose={() => setPathModal((m) => ({ ...m, open: false }))}>
          <div className="space-y-4">
            <Field label={t("certPaths.pathTitle")}>
              <Input value={pathModal.titleFr} onChange={(e) => setPathModal((m) => ({ ...m, titleFr: e.target.value }))} placeholder={t("certPaths.pathTitlePlaceholder")} />
            </Field>
            <Field label={t("certPaths.description")}>
              <Textarea value={pathModal.descriptionFr} onChange={(e) => setPathModal((m) => ({ ...m, descriptionFr: e.target.value }))} rows={3} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("certPaths.level")}>
                <Select value={String(pathModal.level)} onValueChange={(v) => setPathModal((m) => ({ ...m, level: parseInt(v) }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{[1, 2, 3].map((l) => <SelectItem key={l} value={String(l)}>{t("certPaths.level")} {l}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label={t("certPaths.statusLabel")}>
                <Select value={pathModal.status} onValueChange={(v) => setPathModal((m) => ({ ...m, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["draft", "published", "archived"].map((s) => <SelectItem key={s} value={s}>{t(`certPaths.status.${s}`)}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setPathModal((m) => ({ ...m, open: false }))}>{t("common.cancel")}</Button>
              <Button onClick={savePath} disabled={!pathModal.titleFr.trim()}>{t("common.save")}</Button>
            </div>
          </div>
        </ModalShell>
      )}

      {/* ===== Modale Modèles de parcours (instanciation 1 clic) ===== */}
      {templateModal.open && (
        <ModalShell title={t("certPaths.fromTemplate")} onClose={() => setTemplateModal((m) => ({ ...m, open: false }))}>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{t("certPaths.fromTemplateHint")}</p>
            {templateModal.error && (
              <div className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{templateModal.error}</div>
            )}
            {templateModal.loading ? (
              <div className="text-center text-muted-foreground py-8">{t("common.loading")}</div>
            ) : templateModal.list.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">{t("certPaths.noTemplate")}</div>
            ) : (
              <div className="space-y-2">
                {templateModal.list.map((tpl) => {
                  const busy = templateModal.instantiatingId === tpl.id;
                  return (
                    <div key={tpl.id} className="rounded-xl border p-4 flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold truncate" style={{ color: "#384872" }}>{pickFrEn(tpl.nameFr, tpl.nameEn, lang)}</h4>
                          <span className="text-xs rounded-full px-2 py-0.5 bg-violet-100 text-violet-700 shrink-0">{t(`certPaths.kind.${tpl.kind}`, tpl.kind)}</span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">{pickFrEn(tpl.descriptionFr, tpl.descriptionEn, lang)}</p>
                        <div className="text-xs text-muted-foreground mt-2">{t("certPaths.phaseCount", { count: tpl.phaseCount })}</div>
                      </div>
                      <Button size="sm" onClick={() => instantiateTemplate(tpl.id)} disabled={busy || templateModal.instantiatingId !== null}>
                        {busy ? t("common.loading") : t("certPaths.useTemplate")}
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </ModalShell>
      )}

      {/* ===== Assistant « compléter les cas » (lot 2) ===== */}
      {detail && (
        <PathCaseAssistant
          open={assistantOpen}
          phases={detail.phases}
          simulators={simulators}
          accountId={detail.path.clientAccountId ?? undefined}
          initialPhaseId={assistantPhaseId}
          onClose={() => setAssistantOpen(false)}
          onSaved={() => { setAssistantOpen(false); openDetail(detail.path.id); }}
        />
      )}

      {/* ===== Modale Phase (champs clonés du challenge) ===== */}
      {phaseModal.open && (
        <ModalShell title={phaseModal.editingId ? t("certPaths.editPhase") : t("certPaths.addPhase")} onClose={() => setPhaseModal((m) => ({ ...m, open: false }))}>
          <div className="space-y-4">
            <Field label={t("certPaths.phaseTitle")} required>
              <Input value={phaseModal.form.titleFr} onChange={(e) => setForm("titleFr", e.target.value)} />
            </Field>
            <Field label={t("certPaths.description")}>
              <Textarea value={phaseModal.form.descriptionFr} onChange={(e) => setForm("descriptionFr", e.target.value)} rows={2} />
            </Field>
            <Field label={t("challenge.simulator")} required>
              <Select value={phaseModal.form.simulatorId} onValueChange={(v) => setForm("simulatorId", v, true)}>
                <SelectTrigger><SelectValue placeholder={t("challenge.selectSimulator")} /></SelectTrigger>
                <SelectContent>{simulators.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>

            {/* Cas de simulation */}
            {phaseModal.form.simulatorId && (
              <Field label={t("certPaths.cases")}>
                {phaseTargets.length === 0 ? (
                  <p className="text-xs text-muted-foreground">{t("certPaths.noCase")}</p>
                ) : (
                  <div className="space-y-1 max-h-32 overflow-y-auto rounded-lg border p-2">
                    {phaseTargets.map((tg) => {
                      const checked = phaseModal.form.targetIds.includes(tg.id);
                      return (
                        <label key={tg.id} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input type="checkbox" checked={checked} onChange={() => {
                            const ids = checked ? phaseModal.form.targetIds.filter((x) => x !== tg.id) : [...phaseModal.form.targetIds, tg.id];
                            setForm("targetIds", ids);
                          }} />
                          {tg.displayName || tg.industry || tg.id.slice(0, 8)}
                        </label>
                      );
                    })}
                  </div>
                )}
              </Field>
            )}

            <div className="grid grid-cols-2 gap-3">
              <Field label={t("challenge.minSessions")}>
                <Input type="number" min={1} value={phaseModal.form.minSessions} onChange={(e) => setForm("minSessions", e.target.value)} />
              </Field>
              <Field label={t("challenge.maxSessions")}>
                <Input type="number" min={1} value={phaseModal.form.maxSessions} onChange={(e) => setForm("maxSessions", e.target.value)} />
              </Field>
            </div>

            {/* Objectif principal + cible */}
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("challenge.primaryObjective")} required>
                <Select value={phaseModal.form.primaryObjective || undefined} onValueChange={(v) => setForm("primaryObjective", v)} disabled={!phaseModal.form.simulatorId}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{availableObjectives.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              {phaseModal.form.primaryObjective && !BOOLEAN_OBJECTIVES.has(phaseModal.form.primaryObjective) && (
                <Field label={t("certPaths.target")}>
                  <Input type="number" value={phaseModal.form.primaryObjectiveTarget} onChange={(e) => setForm("primaryObjectiveTarget", e.target.value)} />
                </Field>
              )}
            </div>

            {/* Objectif secondaire (bonus) */}
            <div className="grid grid-cols-2 gap-3">
              <Field label={`${t("challenge.secondaryObjective")} (${t("certPaths.bonus")})`}>
                <Select value={phaseModal.form.secondaryObjective || "_none"} onValueChange={(v) => setForm("secondaryObjective", v === "_none" ? "" : v)} disabled={!phaseModal.form.simulatorId}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">—</SelectItem>
                    {availableObjectives.filter((o) => o.value !== phaseModal.form.primaryObjective).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              {phaseModal.form.secondaryObjective && !BOOLEAN_OBJECTIVES.has(phaseModal.form.secondaryObjective) && (
                <Field label={t("certPaths.target")}>
                  <Input type="number" value={phaseModal.form.secondaryObjectiveTarget} onChange={(e) => setForm("secondaryObjectiveTarget", e.target.value)} />
                </Field>
              )}
            </div>

            {/* Difficulté (seuils orange/rouge retirés : hérités par copier-coller du challenge,
                pas de sens pour un parcours certifiant — cf. Fabien 07/07/26) */}
            <Field label={t("certPaths.difficulty")}>
              <Select value={phaseModal.form.difficulty || "_free"} onValueChange={(v) => setForm("difficulty", v === "_free" ? "" : v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_free">{t("certPaths.difficultyFree")}</SelectItem>
                  <SelectItem value="easy">{t("wizard.interChallenge.difficultyEasy")}</SelectItem>
                  <SelectItem value="medium">{t("wizard.interChallenge.difficultyMedium")}</SelectItem>
                  <SelectItem value="hard">{t("wizard.interChallenge.difficultyHard")}</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            {/* Liaison : phase ouverte une fois celle-ci terminée */}
            <Field label={t("certPaths.unlocks")}>
              <Select value={phaseModal.form.unlocksPhaseId || "_seq"} onValueChange={(v) => setForm("unlocksPhaseId", v === "_seq" ? "" : v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_seq">{t("certPaths.unlocksNext")}</SelectItem>
                  {(detail?.phases || [])
                    .filter((p) => p.id !== phaseModal.editingId)
                    .sort((a, b) => a.sequence - b.sequence)
                    .map((p) => <SelectItem key={p.id} value={p.id}>{p.titleFr}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground mt-1">{t("certPaths.unlocksHint")}</p>
            </Field>

            <div className="flex items-center justify-between gap-2 pt-2">
              <p className="text-xs text-muted-foreground">{t("common.requiredFieldsHint")}</p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setPhaseModal((m) => ({ ...m, open: false }))}>{t("common.cancel")}</Button>
                <Button onClick={savePhase} disabled={!phaseModal.form.titleFr.trim() || !phaseModal.form.simulatorId || !phaseModal.form.primaryObjective}>{t("common.save")}</Button>
              </div>
            </div>
          </div>
        </ModalShell>
      )}

      {/* ===== Modale Ajout d'assignations (multi-sélection) ===== */}
      {assignModal.open && (
        <ModalShell title={t("certPaths.addAssignment")} onClose={() => setAssignModal({ open: false, selected: [] })}>
          {(() => {
            const candidates = assignable.filter((u) => !u.enrolled);
            return candidates.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("certPaths.allAssigned")}</p>
            ) : (
              <div className="space-y-3">
                <div className="max-h-72 overflow-y-auto rounded-lg border divide-y">
                  {candidates.map((u) => {
                    const checked = assignModal.selected.includes(u.id);
                    return (
                      <label key={u.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => setAssignModal((m) => ({ ...m, selected: checked ? m.selected.filter((x) => x !== u.id) : [...m.selected, u.id] }))}
                        />
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{u.firstName} {u.lastName}</p>
                          <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{t("certPaths.selectedCount", { count: assignModal.selected.length })}</span>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setAssignModal({ open: false, selected: [] })}>{t("common.cancel")}</Button>
                    <Button onClick={confirmAssign} disabled={assignModal.selected.length === 0}>{t("certPaths.assignSelected")}</Button>
                  </div>
                </div>
              </div>
            );
          })()}
        </ModalShell>
      )}

      {/* ===== Popup d'avertissement — suppression de parcours ===== */}
      {deleteConfirm && (
        <ModalShell title={t("certPaths.deleteTitle")} onClose={() => !deleting && setDeleteConfirm(null)}>
          <div className="space-y-4">
            <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
              <div className="text-sm text-red-800">
                <p className="font-semibold">{pickTitle(deleteConfirm)}</p>
                <p className="mt-1">{t("certPaths.deleteWarning")}</p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDeleteConfirm(null)} disabled={deleting}>{t("common.cancel")}</Button>
              <Button
                onClick={confirmDeletePath}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <Trash2 className="h-4 w-4 mr-1.5" />{deleting ? t("common.loading") : t("certPaths.deleteConfirm")}
              </Button>
            </div>
          </div>
        </ModalShell>
      )}

      {/* ===== Popup d'avertissement — suppression de phase ===== */}
      {deletePhaseConfirm && (
        <ModalShell title={t("certPaths.deletePhaseTitle")} onClose={() => !deleting && setDeletePhaseConfirm(null)}>
          <div className="space-y-4">
            <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
              <div className="text-sm text-red-800">
                <p className="font-semibold">{deletePhaseConfirm.titleFr}</p>
                <p className="mt-1">{t("certPaths.deletePhaseWarning")}</p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDeletePhaseConfirm(null)} disabled={deleting}>{t("common.cancel")}</Button>
              <Button onClick={confirmDeletePhase} disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white">
                <Trash2 className="h-4 w-4 mr-1.5" />{deleting ? t("common.loading") : t("certPaths.deleteConfirm")}
              </Button>
            </div>
          </div>
        </ModalShell>
      )}
    </div>
  );

  function setForm(key: keyof typeof EMPTY_PHASE, value: unknown, resetObjectives = false) {
    setPhaseModal((m) => ({
      ...m,
      form: {
        ...m.form,
        [key]: value as never,
        ...(resetObjectives ? { primaryObjective: "", secondaryObjective: "", targetIds: [] } : {}),
      },
    }));
  }
}

// --- Petits composants utilitaires ---
function Field({ label, children, required }: { label: string; children: React.ReactNode; required?: boolean }) {
  return (
    <div>
      <Label className="text-xs">
        {label}
        {required && <span className="text-red-500 ml-0.5" aria-hidden="true">*</span>}
      </Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function ModalShell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/70" onClick={onClose} />
      <div className="fixed left-1/2 top-1/2 z-50 w-[95vw] max-w-2xl max-h-[88vh] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border bg-background p-6 shadow-lg">
        <button className="absolute right-4 top-4 opacity-70 hover:opacity-100" onClick={onClose}><X className="h-4 w-4" /></button>
        <h2 className="mb-4 text-lg font-semibold" style={{ color: "#384872" }}>{title}</h2>
        {children}
      </div>
    </>
  );
}

/** Diagnostic de fin d'évaluation : profil de compétence ISOLÉ aux 5 sessions du
 *  parcours (instantané figé), et non le graphe global de l'utilisateur. */
function EvalDiagnostic({ pathId }: { pathId: string }) {
  const [profile, setProfile] = useState<CompetencyPhase[] | null>(null);
  useEffect(() => {
    let alive = true;
    competencyApi.getEvaluationDiagnostic(pathId)
      .then((d) => { if (alive) setProfile(d.phases); })
      .catch(() => { if (alive) setProfile([]); });
    return () => { alive = false; };
  }, [pathId]);
  if (!profile) return <div className="py-6 text-center text-xs text-muted-foreground">…</div>;
  return <CompetencyChart profile={profile} />;
}
